import {gql, useQuery} from '@apollo/client'
import {useEffect, useRef, useState} from 'react'
import {bugsnagClient} from '~/bugSnag'
import {CurrentOrganisation} from './CurrentOrganisationContext'
import {Token} from './TokenContext'
import {IdentifyUserData} from './__types__/IdentifyUserData'

const IDENTIFY_USER_DATA = gql`
    query IdentifyUserData {
        me {
            idStr
            email
            firstName
            lastName
            subId
        }
    }
`

export function useAnalyticsUserId() {
    const [analyticsId, setAnalyticsId] = useState<string | null>(null)
    useEffect(() => {
        const timerId = setInterval(() => {
            const userId = window.analytics?.user?.().id() ?? null
            setAnalyticsId((prevId) => {
                // Only update if the value actually changed
                if (userId !== prevId) {
                    return userId
                }
                return prevId
            })
        }, 100)

        return () => clearInterval(timerId)
    }, []) // Remove analyticsId from dependencies to avoid recreating interval

    return analyticsId
}

export const useIdentifyUser = () => {
    const {token} = Token.useContainer()
    const {currentOrganisation} = CurrentOrganisation.useContainer()
    const {data, refetch} = useQuery<IdentifyUserData>(IDENTIFY_USER_DATA, {
        fetchPolicy: 'network-only',
        skip: !token,
    })
    const userAnalyticsId = useAnalyticsUserId()

    useEffect(() => {
        if (token) refetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    const user = data?.me

    useEffect(() => {
        window.analytics?.ready(() => {
            // Disable mixpanel geo-location data tracking
            if (window?.mixpanel?.get_config('ip')) window.mixpanel.set_config({ip: !window.mixpanel?.get_config('ip')})

            if (token && user && currentOrganisation) {
                // Migrate to using Sub ID if we're currently using
                // their Self Build User ID
                if (window.analytics?.user().id() === user.idStr) {
                    window.analytics?.alias(user.idStr)
                }

                window.analytics?.identify(user.idStr, {
                    name: `${user.firstName} ${user.lastName}`,
                    email: user.email,
                    idStr: user.idStr,
                    subjectId: user.subId,
                    organisationId: currentOrganisation.idStr,
                    currentOrganisation: currentOrganisation.name,
                })
            }
        })
    }, [user, token, currentOrganisation])

    // Track previous values to avoid unnecessary Bugsnag calls
    const prevUserRef = useRef<typeof user>(undefined)
    const prevUserAnalyticsIdRef = useRef<string | null>(null)

    useEffect(() => {
        if (!bugsnagClient) return

        // Only call Bugsnag if values have actually changed
        const prevUser = prevUserRef.current
        const prevAnalyticsId = prevUserAnalyticsIdRef.current

        const userChanged =
            user?.idStr !== prevUser?.idStr ||
            user?.email !== prevUser?.email ||
            user?.firstName !== prevUser?.firstName ||
            user?.subId !== prevUser?.subId

        const analyticsIdChanged = userAnalyticsId !== prevAnalyticsId

        // Only proceed if something actually changed
        if (!userChanged && !analyticsIdChanged && user === prevUser && userAnalyticsId === prevAnalyticsId) {
            return
        }

        if (user) {
            // User takes precedence - only update Bugsnag if user changed
            if (userChanged) {
                bugsnagClient.setUser(user.idStr, user.email || undefined, user.firstName)
                bugsnagClient.addMetadata('user', 'subscriberId', user.subId)
            }
            prevUserRef.current = user
            prevUserAnalyticsIdRef.current = null
        } else if (userAnalyticsId) {
            // Only update Bugsnag if analytics ID changed
            if (analyticsIdChanged) {
                bugsnagClient.setUser(userAnalyticsId)
                bugsnagClient.addMetadata('user', 'userAnalyticsId', userAnalyticsId)
            }
            prevUserAnalyticsIdRef.current = userAnalyticsId
            prevUserRef.current = undefined
        } else if (prevUser || prevAnalyticsId !== null) {
            // Clear Bugsnag user if both are now null/undefined but we had one before
            bugsnagClient.setUser()
            prevUserRef.current = undefined
            prevUserAnalyticsIdRef.current = null
        }
    }, [user, userAnalyticsId])
}
